<script setup lang="ts">
import type {
  PublicEventDetails,
  PublicEventListItem,
  SemesterListItem,
  UserRegistrationListItem,
} from '@/types'

import { signMeUpSupportEmail } from '@/constants/contact'
import { eventService } from '@/services/eventService'
import { getMetadata } from '@/services/metadataService'
import userService from '@/services/userService'

definePage({
  meta: {
    requiresAuth: false,
  },
})

type ContactKind = 'registration' | 'event' | 'technical'

type SelectOption = {
  label: string
  value: number
}

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { setPageTitle } = useBreadcrumbs()
const { isAuthenticated } = useUser()

const selectedKind = ref<ContactKind | null>(null)

const metadataSemesters = ref<SemesterListItem[]>([])
const defaultPublishedSemesterId = ref<number | null>(null)

const registrations = ref<UserRegistrationListItem[]>([])
const publicEvents = ref<PublicEventListItem[]>([])
const eventDetailsCache = ref<Record<number, PublicEventDetails>>({})

const selectedRegistrationSemesterId = ref<number | null>(null)
const selectedEventSemesterId = ref<number | null>(null)
const selectedRegistrationId = ref<number | null>(null)
const selectedEventId = ref<number | null>(null)

const metadataLoading = ref(false)
const registrationsLoading = ref(false)
const publicEventsLoading = ref(false)
const eventDetailsLoading = ref(false)

const metadataError = ref<string | null>(null)
const registrationsError = ref<string | null>(null)
const publicEventsError = ref<string | null>(null)
const eventDetailsError = ref<string | null>(null)

const kindOptions = computed(() => [
  {
    kind: 'registration' as const,
    icon: 'i-lucide-bookmark',
    title: t('pages.contact.types.registration.title'),
    description: t('pages.contact.types.registration.description'),
  },
  {
    kind: 'event' as const,
    icon: 'i-lucide-calendar',
    title: t('pages.contact.types.event.title'),
    description: t('pages.contact.types.event.description'),
  },
  {
    kind: 'technical' as const,
    icon: 'i-lucide-wrench',
    title: t('pages.contact.types.technical.title'),
    description: t('pages.contact.types.technical.description'),
  },
])

const isGerman = computed(() => locale.value.startsWith('de'))

function parseKind(value: unknown): ContactKind | null {
  if (typeof value !== 'string') {
    return null
  }

  return value === 'registration' || value === 'event' || value === 'technical' ? value : null
}

function getLocalizedSemesterLabel(semester: {
  nameDE?: string | null
  nameEN?: string | null
  fullNameDE?: string | null
  fullNameEN?: string | null
}): string {
  if (isGerman.value) {
    return semester.fullNameDE || semester.nameDE || semester.fullNameEN || semester.nameEN || ''
  }

  return semester.fullNameEN || semester.nameEN || semester.fullNameDE || semester.nameDE || ''
}

function getRegistrationLabel(registration: UserRegistrationListItem): string {
  return isGerman.value
    ? registration.eventNameDE
    : registration.eventNameEN || registration.eventNameDE
}

function getEventLabel(event: PublicEventListItem): string {
  return isGerman.value ? event.nameDE : event.nameEN || event.nameDE
}

function pickPreferredOptionId(
  options: SelectOption[],
  preferredId?: number | null,
): number | null {
  if (preferredId && options.some((option) => option.value === preferredId)) {
    return preferredId
  }

  return options[0]?.value ?? null
}

async function syncKindQuery(kind: ContactKind | null) {
  const currentKind = parseKind(
    Array.isArray(route.query.kind) ? route.query.kind[0] : route.query.kind,
  )
  if (currentKind === kind) {
    return
  }

  const query = { ...route.query }
  if (kind) {
    query.kind = kind
  } else {
    delete query.kind
  }

  await router.replace({ query })
}

async function loadMetadata() {
  metadataLoading.value = true
  metadataError.value = null

  try {
    const response = await getMetadata()
    metadataSemesters.value = response.semesters.semesters

    const publishedSemesters = response.semesters.semesters.filter(
      (semester) => semester.isPublished,
    )
    defaultPublishedSemesterId.value = pickPreferredOptionId(
      publishedSemesters.map((semester) => ({
        label: getLocalizedSemesterLabel(semester),
        value: semester.id,
      })),
      response.semesters.defaultSemesterId ?? response.semesters.activeSemesterId ?? null,
    )

    if (selectedEventSemesterId.value === null) {
      selectedEventSemesterId.value = defaultPublishedSemesterId.value
    }
  } catch (error) {
    console.error('Error loading contact metadata:', error)
    metadataError.value = t('pages.contact.errors.metadata')
  } finally {
    metadataLoading.value = false
  }
}

async function loadRegistrations() {
  if (!isAuthenticated.value) {
    registrations.value = []
    registrationsError.value = null
    return
  }

  registrationsLoading.value = true
  registrationsError.value = null

  try {
    registrations.value = await userService.getRegistrations()
  } catch (error) {
    console.error('Error loading registrations for contact page:', error)
    registrationsError.value = t('pages.contact.errors.registrations')
  } finally {
    registrationsLoading.value = false
  }
}

async function loadPublicEvents(semesterId: number) {
  publicEventsLoading.value = true
  publicEventsError.value = null

  try {
    publicEvents.value = await eventService.getPublicEvents({
      semesterId,
      excludeTemplates: true,
    })
  } catch (error) {
    console.error('Error loading public events for contact page:', error)
    publicEventsError.value = t('pages.contact.event.loadError')
  } finally {
    publicEventsLoading.value = false
  }
}

async function loadEventDetails(eventId: number) {
  if (eventDetailsCache.value[eventId]) {
    eventDetailsError.value = null
    return
  }

  eventDetailsLoading.value = true
  eventDetailsError.value = null

  try {
    const details = await eventService.getPublicEventDetails(eventId)
    eventDetailsCache.value = {
      ...eventDetailsCache.value,
      [eventId]: details,
    }
  } catch (error) {
    console.error('Error loading event contact details:', error)
    eventDetailsError.value = t('pages.contact.event.detailsLoadError')
  } finally {
    eventDetailsLoading.value = false
  }
}

const publishedSemesterOptions = computed<SelectOption[]>(() =>
  metadataSemesters.value
    .filter((semester) => semester.isPublished)
    .map((semester) => ({
      label: getLocalizedSemesterLabel(semester),
      value: semester.id,
    })),
)

const registrationSemesterOptions = computed<SelectOption[]>(() => {
  const options = metadataSemesters.value
    .filter((semester) =>
      registrations.value.some((registration) => registration.semesterId === semester.id),
    )
    .map((semester) => ({
      label: getLocalizedSemesterLabel(semester),
      value: semester.id,
    }))

  const seenIds = new Set(options.map((option) => option.value))
  const missingOptions = registrations.value
    // Registrations can outlive the published metadata list for older semesters.
    .filter((registration) => registration.semesterId && !seenIds.has(registration.semesterId))
    .map((registration) => ({
      label:
        (isGerman.value ? registration.semesterNameDE : registration.semesterNameEN) ||
        registration.semesterNameDE ||
        registration.semesterNameEN ||
        String(registration.semesterId),
      value: registration.semesterId!,
    }))
    .filter(
      (option, index, allOptions) =>
        allOptions.findIndex((candidate) => candidate.value === option.value) === index,
    )

  return [...options, ...missingOptions]
})

const filteredRegistrations = computed(() => {
  if (!selectedRegistrationSemesterId.value) {
    return [] as UserRegistrationListItem[]
  }

  return registrations.value.filter(
    (registration) => registration.semesterId === selectedRegistrationSemesterId.value,
  )
})

const registrationOptions = computed<SelectOption[]>(() =>
  [...filteredRegistrations.value]
    .sort((left, right) => getRegistrationLabel(left).localeCompare(getRegistrationLabel(right)))
    .map((registration) => ({
      label: getRegistrationLabel(registration),
      value: registration.registrationId,
    })),
)

const eventOptions = computed<SelectOption[]>(() =>
  [...publicEvents.value]
    .sort((left, right) => getEventLabel(left).localeCompare(getEventLabel(right)))
    .map((event) => ({
      label: getEventLabel(event),
      value: event.id,
    })),
)

const selectedRegistration = computed(
  () =>
    filteredRegistrations.value.find(
      (registration) => registration.registrationId === selectedRegistrationId.value,
    ) ?? null,
)

const selectedPublicEvent = computed(
  () => publicEvents.value.find((event) => event.id === selectedEventId.value) ?? null,
)

const selectedEventDetails = computed(() =>
  selectedEventId.value ? (eventDetailsCache.value[selectedEventId.value] ?? null) : null,
)

const registrationResultSubtitle = computed(() =>
  selectedRegistration.value ? getRegistrationLabel(selectedRegistration.value) : '',
)

const eventResultSubtitle = computed(() =>
  selectedPublicEvent.value ? getEventLabel(selectedPublicEvent.value) : '',
)

function selectKind(kind: ContactKind) {
  selectedKind.value = kind
}

function goToLogin() {
  void router.push({
    path: '/user/login',
    query: {
      returnUrl: '/contact?kind=registration',
    },
  })
}

watch(
  () => route.query.kind,
  (value) => {
    const normalized = parseKind(Array.isArray(value) ? value[0] : value)
    if (selectedKind.value !== normalized) {
      selectedKind.value = normalized
    }
  },
  { immediate: true },
)

watch(selectedKind, (kind) => {
  void syncKindQuery(kind)
})

watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void loadRegistrations()
    } else {
      registrations.value = []
      registrationsError.value = null
      selectedRegistrationSemesterId.value = null
      selectedRegistrationId.value = null
    }
  },
  { immediate: true },
)

watch(
  registrationSemesterOptions,
  (options) => {
    const preferredId = defaultPublishedSemesterId.value
    if (!options.some((option) => option.value === selectedRegistrationSemesterId.value)) {
      selectedRegistrationSemesterId.value = pickPreferredOptionId(options, preferredId)
    }
  },
  { immediate: true },
)

watch(
  publishedSemesterOptions,
  (options) => {
    if (!options.some((option) => option.value === selectedEventSemesterId.value)) {
      selectedEventSemesterId.value = pickPreferredOptionId(
        options,
        defaultPublishedSemesterId.value,
      )
    }
  },
  { immediate: true },
)

watch(filteredRegistrations, (items) => {
  if (!items.some((registration) => registration.registrationId === selectedRegistrationId.value)) {
    selectedRegistrationId.value = null
  }
})

watch(publicEvents, (items) => {
  if (!items.some((event) => event.id === selectedEventId.value)) {
    selectedEventId.value = null
  }
})

watch(
  [selectedKind, selectedEventSemesterId],
  ([kind, semesterId]) => {
    if (kind !== 'event' || !semesterId) {
      return
    }

    void loadPublicEvents(semesterId)
  },
  { immediate: true },
)

watch(selectedEventId, (eventId) => {
  if (!eventId) {
    eventDetailsError.value = null
    return
  }

  void loadEventDetails(eventId)
})

onMounted(() => {
  setPageTitle('pages.contact.title')
  void loadMetadata()
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <UCard>
      <template #header>
        {{ t('pages.contact.title') }}
      </template>
      <template #default>
        <div class="space-y-6">
          <p class="m-0 text-neutral-600 dark:text-neutral-300">
            {{ t('pages.contact.intro') }}
          </p>

          <div class="space-y-3">
            <h2 class="m-0 text-lg font-semibold">
              {{ t('pages.contact.question') }}
            </h2>

            <div class="grid gap-4 lg:grid-cols-3">
              <UCard
                v-for="option in kindOptions"
                :key="option.kind"
                :class="[
                  'cursor-pointer border transition-colors',
                  selectedKind === option.kind
                    ? 'border-primary ring-1 ring-primary'
                    : 'border-neutral-200 hover:border-primary/50 dark:border-neutral-700',
                ]"
              >
                <template #default>
                  <div
                    class="flex h-full flex-col gap-3"
                    role="button"
                    tabindex="0"
                    @click="selectKind(option.kind)"
                    @keydown.enter.prevent="selectKind(option.kind)"
                    @keydown.space.prevent="selectKind(option.kind)"
                  >
                    <div class="flex items-center gap-3">
                      <span
                        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-[var(--app-text-primary)]"
                      >
                        <UIcon :name="option.icon" />
                      </span>
                      <h3 class="m-0 text-base font-semibold">
                        {{ option.title }}
                      </h3>
                    </div>
                    <p class="m-0 text-sm text-neutral-600 dark:text-neutral-300">
                      {{ option.description }}
                    </p>
                  </div>
                </template>
              </UCard>
            </div>
          </div>

          <div
            v-if="metadataError && (selectedKind === 'event' || selectedKind === 'registration')"
            class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {{ metadataError }}
          </div>

          <UCard v-if="selectedKind === 'registration'">
            <template #header>
              {{ t('pages.contact.registration.title') }}
            </template>
            <template #default>
              <div class="space-y-4">
                <p class="m-0 text-neutral-600 dark:text-neutral-300">
                  {{ t('pages.contact.registration.description') }}
                </p>

                <div v-if="!isAuthenticated" class="space-y-3">
                  <div
                    class="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-4 dark:border-neutral-700 dark:bg-neutral-900"
                  >
                    <h3 class="m-0 mb-2 text-base font-semibold">
                      {{ t('pages.contact.registration.loginTitle') }}
                    </h3>
                    <p class="m-0 text-sm text-neutral-600 dark:text-neutral-300">
                      {{ t('pages.contact.registration.loginDescription') }}
                    </p>
                  </div>
                  <UButton
                    :label="t('pages.contact.registration.loginAction')"
                    icon="i-lucide-log-in"
                    @click="goToLogin"
                  />
                </div>

                <template v-else>
                  <div
                    v-if="registrationsError"
                    class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {{ registrationsError }}
                  </div>

                  <p v-else-if="registrationsLoading" class="m-0 text-sm text-muted">
                    {{ t('pages.contact.loading.registrations') }}
                  </p>

                  <p
                    v-else-if="registrationSemesterOptions.length === 0"
                    class="m-0 text-sm text-muted"
                  >
                    {{ t('pages.contact.registration.empty') }}
                  </p>

                  <template v-else>
                    <div class="grid gap-4 lg:grid-cols-2">
                      <div class="space-y-2">
                        <label class="text-sm font-medium">
                          {{ t('pages.contact.labels.semester') }}
                        </label>
                        <AppSelect
                          v-model="selectedRegistrationSemesterId"
                          :options="registrationSemesterOptions"
                          option-label="label"
                          option-value="value"
                          :placeholder="t('pages.contact.placeholders.semester')"
                          class="w-full"
                        />
                      </div>

                      <div class="space-y-2">
                        <label class="text-sm font-medium">
                          {{ t('pages.contact.labels.registration') }}
                        </label>
                        <AppSelect
                          v-model="selectedRegistrationId"
                          :options="registrationOptions"
                          option-label="label"
                          option-value="value"
                          :placeholder="t('pages.contact.placeholders.registration')"
                          :disabled="registrationOptions.length === 0"
                          filter
                          class="w-full"
                        />
                      </div>
                    </div>

                    <p
                      v-if="selectedRegistrationSemesterId && registrationOptions.length === 0"
                      class="m-0 text-sm text-muted"
                    >
                      {{ t('pages.contact.registration.noRegistrationsInSemester') }}
                    </p>

                    <UCard
                      v-if="selectedRegistration"
                      class="border border-neutral-200 shadow-none dark:border-neutral-700"
                    >
                      <template #header>
                        {{ t('pages.contact.result.registrationTitle') }}
                      </template>
                      <template #subtitle>
                        {{ registrationResultSubtitle }}
                      </template>
                      <template #default>
                        <ContactDetailsContent
                          :contact-email="selectedRegistration.contactEmail"
                          :lecturer-display-name="selectedRegistration.lecturerDisplayName"
                          :missing-message="t('pages.contact.result.noContact')"
                        />
                      </template>
                    </UCard>
                  </template>
                </template>
              </div>
            </template>
          </UCard>

          <UCard v-else-if="selectedKind === 'event'">
            <template #header>
              {{ t('pages.contact.event.title') }}
            </template>
            <template #default>
              <div class="space-y-4">
                <p class="m-0 text-neutral-600 dark:text-neutral-300">
                  {{ t('pages.contact.event.description') }}
                </p>

                <p
                  v-if="metadataLoading && publishedSemesterOptions.length === 0"
                  class="m-0 text-sm text-muted"
                >
                  {{ t('pages.contact.loading.metadata') }}
                </p>

                <template v-else>
                  <div class="grid gap-4 lg:grid-cols-2">
                    <div class="space-y-2">
                      <label class="text-sm font-medium">
                        {{ t('pages.contact.labels.semester') }}
                      </label>
                      <AppSelect
                        v-model="selectedEventSemesterId"
                        :options="publishedSemesterOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="t('pages.contact.placeholders.semester')"
                        :disabled="publishedSemesterOptions.length === 0"
                        class="w-full"
                      />
                    </div>

                    <div class="space-y-2">
                      <label class="text-sm font-medium">
                        {{ t('pages.contact.labels.event') }}
                      </label>
                      <AppSelect
                        v-model="selectedEventId"
                        :options="eventOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="t('pages.contact.placeholders.event')"
                        :disabled="eventOptions.length === 0"
                        filter
                        class="w-full"
                      />
                    </div>
                  </div>

                  <div
                    v-if="publicEventsError"
                    class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {{ publicEventsError }}
                  </div>

                  <p v-else-if="publicEventsLoading" class="m-0 text-sm text-muted">
                    {{ t('pages.contact.loading.events') }}
                  </p>

                  <p
                    v-else-if="selectedEventSemesterId && eventOptions.length === 0"
                    class="m-0 text-sm text-muted"
                  >
                    {{ t('pages.contact.event.empty') }}
                  </p>

                  <div
                    v-if="eventDetailsError"
                    class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                  >
                    {{ eventDetailsError }}
                  </div>

                  <p v-else-if="eventDetailsLoading" class="m-0 text-sm text-muted">
                    {{ t('pages.contact.loading.contact') }}
                  </p>

                  <UCard
                    v-if="selectedEventDetails"
                    class="border border-neutral-200 shadow-none dark:border-neutral-700"
                  >
                    <template #header>
                      {{ t('pages.contact.result.eventTitle') }}
                    </template>
                    <template #subtitle>
                      {{ eventResultSubtitle }}
                    </template>
                    <template #default>
                      <ContactDetailsContent
                        :contact-email="selectedEventDetails.contactEmail"
                        :lecturer-display-name="selectedEventDetails.lecturerDisplayName"
                        :missing-message="t('pages.contact.result.noContact')"
                      />
                    </template>
                  </UCard>
                </template>
              </div>
            </template>
          </UCard>

          <UCard v-else-if="selectedKind === 'technical'">
            <template #header>
              {{ t('pages.contact.technical.title') }}
            </template>
            <template #default>
              <div class="space-y-4">
                <p class="m-0 text-neutral-600 dark:text-neutral-300">
                  {{ t('pages.contact.technical.description') }}
                </p>

                <UCard class="border border-neutral-200 shadow-none dark:border-neutral-700">
                  <template #header>
                    {{ t('pages.contact.result.technicalTitle') }}
                  </template>
                  <template #default>
                    <a
                      :href="`mailto:${signMeUpSupportEmail}`"
                      class="break-all text-[var(--app-text-primary)] hover:underline"
                    >
                      {{ signMeUpSupportEmail }}
                    </a>
                  </template>
                </UCard>

                <div
                  class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
                >
                  {{ t('pages.contact.technical.supportNote') }}
                </div>
              </div>
            </template>
          </UCard>
        </div>
      </template>
    </UCard>
  </div>
</template>
